import { createContext, type PropsWithChildren, use, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { noop } from '@interxfi/utilities/fn/no-op';
import { clone } from '@interxfi/utilities/object/clone';
import { useFormWizardResetSteps, useFormWizardSetStepValue } from '../form-wizard-context';
import type { BaseFormWizardStepGeneric } from './form-wizard-step';

interface IFormWizardSeedContext<TSteps extends BaseFormWizardStepGeneric> {
  hasSeedData: boolean;
  seedData: (data: TSteps | ((priorValues: TSteps) => TSteps | Promise<TSteps>)) => void;
  clearPreSeedSnapshot: VoidFunction;
  undoSeed: VoidFunction;
}

const FormWizardSeedContext = createContext<IFormWizardSeedContext<any>>({
  hasSeedData: false,
  clearPreSeedSnapshot: noop,
  seedData: noop,
  undoSeed: noop,
});

export function FormWizardSeedContextProvider<
  TSteps extends BaseFormWizardStepGeneric,
  TKey extends keyof TSteps,
  TContext extends object | undefined = undefined,
>({ children, id }: PropsWithChildren<{ id: TKey }>) {
  const resetValues = useFormWizardResetSteps<TSteps, TContext>();
  const setValue = useFormWizardSetStepValue<TSteps, TContext>();
  const { getValues, reset } = useFormContext<TSteps[TKey], TContext, TSteps[TKey]>();
  const [preSeedSnapshot, setPreSeedSnapshot] = useState<TSteps | null>(null);

  const clearPreSeedSnapshot = useCallback(() => {
    setPreSeedSnapshot(null);
  }, []);

  const seedData = useCallback(
    async (data: TSteps | ((priorValues: TSteps) => TSteps | Promise<TSteps>)) => {
      const currentValues = clone(setValue(id, getValues()));

      setPreSeedSnapshot(currentValues);

      const seededData = typeof data === 'function' ? await data(currentValues) : data;

      resetValues(seededData);
      reset(seededData[id], { keepDefaultValues: true });
    },
    [setValue, id, getValues, resetValues, reset],
  );

  const undoSeed = useCallback(() => {
    if (preSeedSnapshot) {
      resetValues(preSeedSnapshot);
      setPreSeedSnapshot(null);
    }
  }, [preSeedSnapshot, resetValues]);

  const hasSeedData = Boolean(preSeedSnapshot);

  const value = useMemo(
    () => ({
      clearPreSeedSnapshot,
      hasSeedData,
      seedData,
      undoSeed,
    }),
    [hasSeedData, seedData, undoSeed, clearPreSeedSnapshot],
  );

  return <FormWizardSeedContext.Provider value={value}>{children}</FormWizardSeedContext.Provider>;
}

export function useFormWizardSeedContext<TSteps extends BaseFormWizardStepGeneric>(): IFormWizardSeedContext<TSteps> {
  const context = use(FormWizardSeedContext);
  if (!context) {
    throw new Error('useFormWizardSeedContext must be used within a FormWizardSeedContextProvider');
  }

  return context;
}
